import { ApiClient, PagedResults, ServiceResponse, ServiceResponseNoContent } from "../../../common";
import { PlatformSettings } from "../../../settings/platformSettings";
import CreateAppDto from "../../dtos/in/createAppDto";
import UpdateAppDto from "../../dtos/in/updateAppDto";
import AppDto from "../../dtos/out/appDto";
import NewAppDto from "../../dtos/out/newAppDto";
import ResetAppSecretDto from "../../dtos/out/resetAppSecretDto";
import { AppIdentityType } from "../../enums/appIdentityType";
import { IApps } from "../interfaces/apps";

export default class Apps extends ApiClient implements IApps {

    private _baseUrl: string;
    constructor(private platformSettings: PlatformSettings) {
        super();
        this._baseUrl = platformSettings.apiUrl + "/auth";
    }

    get baseUrl(): string {
        return this._baseUrl;
    }


    public async createApp(createAppDto: CreateAppDto, accessToken: string): Promise<ServiceResponse<NewAppDto>> {
        return await super.Post<CreateAppDto, NewAppDto>(this.baseUrl, "apps", createAppDto, accessToken);
    }

    public async updateApp(appId: number, updateAppDto: UpdateAppDto, accessToken: string): Promise<ServiceResponse<AppDto>> {
        return await super.Put<UpdateAppDto, AppDto>(this.baseUrl, `apps/${appId}`, updateAppDto, accessToken);
    }

    public async getAppById(appId: number, accessToken: string): Promise<ServiceResponse<AppDto>> {
        return await super.Get<AppDto>(this.baseUrl, `apps/${appId}`, accessToken);
    }

    public async deleteApp(appId: number, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `apps/${appId}`, accessToken);
    }

    public async resetAppSecret(appId: number, accessToken: string): Promise<ServiceResponse<ResetAppSecretDto>> {
        return await super.Post<null, ResetAppSecretDto>(this.baseUrl, `apps/${appId}/app-secret`, null, accessToken);
    }

    public async addAppIdentity(appId: number, identityType: AppIdentityType, externalId: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Put(this.baseUrl, `apps/${appId}/identities/${identityType}/${externalId}`, null, accessToken);
    }

    public async removeAppIdentity(appId: number, identityType: AppIdentityType, externalId: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `apps/${appId}/identities/${identityType}/${externalId}`, accessToken);
    }

    public async addAppAuthScope(appId: number, authScope: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Put(this.baseUrl, `apps/${appId}/auth-scopes/${authScope}`, null, accessToken);
    }

    public async removeAppAuthScope(appId: number, authScope: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `apps/${appId}/auth-scopes/${authScope}`, accessToken);
    }

    public async replaceAppAuthScopes(appId: number, authScopes: string[], accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Put(this.baseUrl, `apps/${appId}/auth-scopes`, authScopes, accessToken);
    }

    public async addAppRole(appId: number, roleId: number, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Put(this.baseUrl, `apps/${appId}/roles/${roleId}`, null, accessToken);
    }

    public async removeAppRole(appId: number, roleId: number, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `apps/${appId}/roles/${roleId}`, accessToken);
    }

    public async searchApps(page: number = 1,
        pageSize: number = 25,
        name: string = null,
        accessToken: string): Promise<ServiceResponse<PagedResults<AppDto>>> {
        let endpoint = `apps?page=${page}&pageSize=${pageSize}`;
        if (name != null) {
            endpoint += `&name=${name}`;
        }
        return await super.Get<PagedResults<AppDto>>(
            this.baseUrl,
            endpoint,
            accessToken);
    }
}

export { Apps };